import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import type { ConsolidatedEventCluster, UserAssertion } from '@/types';
import { formatDecisionSummary, getJudgementTextColor } from '@/types';
import { ReviewedOutcomeDiffLine } from '@/components/review/ComparedReviewedPart';

function judgementShortLabel(judgement?: UserAssertion['judgement']): string {
  if (judgement === 'correct') return '正確';
  if (judgement === 'incorrect') return '錯誤';
  if (judgement === 'uncertain') return '灰色';
  return '—';
}

function judgementTextColor(judgement?: UserAssertion['judgement']): string {
  return getJudgementTextColor(judgement === 'incorrect' ? 'wrong' : judgement);
}

export function eventOnFieldLabel(
  cluster: ConsolidatedEventCluster,
  ownerAssertion: UserAssertion | null
): string {
  const summary = formatDecisionSummary(
    ownerAssertion?.onFieldBall || cluster.onFieldBall,
    ownerAssertion?.onFieldSanction || cluster.dominantSanction
  );
  return summary && summary !== '—' ? summary : '—';
}

export function ConsensusOpinionCell({
  assertion,
  ownerAssertion,
  ownerAssertions,
  highlightReviewedDiff = false,
  canEdit,
  onEdit,
}: {
  assertion: UserAssertion | null;
  ownerAssertion?: UserAssertion | null;
  ownerAssertions?: UserAssertion[];
  highlightReviewedDiff?: boolean;
  canEdit?: boolean;
  onEdit?: (e: React.MouseEvent<HTMLButtonElement>, assertion: UserAssertion) => void;
}) {
  if (!assertion) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem' }}>
        —
      </Typography>
    );
  }

  const label = judgementShortLabel(assertion.judgement);

  return (
    <Stack direction="row" alignItems="flex-start" spacing={0.25} sx={{ minWidth: 0 }}>
      <Stack spacing={0.35} sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ fontSize: '0.85rem', color: judgementTextColor(assertion.judgement) }}
        >
          {label}
        </Typography>
        <ReviewedOutcomeDiffLine
          assertion={assertion}
          ownerAssertion={ownerAssertion ?? null}
          ownerAssertions={ownerAssertions}
          highlightDiff={highlightReviewedDiff}
        />
        {assertion.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.3 }}>
            {assertion.notes}
          </Typography>
        )}
      </Stack>
      {canEdit && onEdit && (
        <Tooltip title="編輯標記">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(e, assertion);
            }}
            sx={{ p: 0.25, mt: -0.25, flexShrink: 0 }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}
